import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DATA_ANALYTICS, DATA_OBJECT, DATA_REASON, DATA_TYPE } from '../../../../const/analytics';
import { FAL_EXCLAMATION_CIRCLE, FAR_ANGLE_DOWN, FAR_ANGLE_UP, FAR_DOLLAR_SIGN } from '../../../../const/iconProps';
import { paymentOptions } from '../../../../const/paymentOptions';
import MainContext from '../../../../Context';
import { getPaymentAmountFromOption, isMonthly, isPN4, isSecuredInstallmentLoanProduct, isSetpayLegacyProduct, isWaivedInterestOneTimePaymentEligible } from '../../../../helpers';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import { useDigitalCollections } from '../../../../hooks/useDigitalCollections';
import { PAYMENT_AMOUNT_INPUT_NAME } from '../../../../machines/oneTimePayment/paymentAmountConfig';
import { PAYMENT_OPTION_INPUT_NAME } from '../../../../machines/oneTimePayment/paymentOptionConfig';
import { t } from '../../../../strings/i18n';
import SelectWithFocus from '../../../../ui/atoms/SelectWithFocus';
import { useField } from '../../../../xform';
import { MAPTextfield, OtherOption, SelectWrapper } from '../subcomponents';
import getOneTimePaymentAmountHelpText from './helpers/getOneTimePaymentAmountHelpText';
import { createPaymentAmountOption, getOneTimeOtherAmountHelpText } from './helpers';
const getAmountOptions = (apiContext) => {
    const accountSummary = apiContext.accountSummaryQueryResult.data;
    const enterpriseEligibility = apiContext.paymentEnterpriseEligibilityQueryResult.data;
    const productType = accountSummary.product.type;
    const otherAmountOption = {
        element: (_jsx(OtherOption, Object.assign({ "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-cy": "other-amount-option", "data-object": DATA_OBJECT.BUTTON, "data-reason": DATA_REASON.PAYMENT_AMOUNT.OTHER_AMOUNT, "data-type": DATA_TYPE.MAKE_A_PAYMENT }, { children: t(`makePayment.${paymentOptions.OTHER_PAYMENT}`) }))),
        value: paymentOptions.OTHER_PAYMENT,
        label: t(`makePayment.${paymentOptions.OTHER_PAYMENT}`)
    };
    if (isMonthly(productType)) {
        const monthlyNextAmountDueOption = createPaymentAmountOption(paymentOptions.MONTHLY_NEXT_AMOUNT_DUE, apiContext);
        const enrolledInWaivedInterest = isWaivedInterestOneTimePaymentEligible(enterpriseEligibility);
        if (enrolledInWaivedInterest) {
            const waivedInterestOption = createPaymentAmountOption(paymentOptions.WAIVED_INTEREST_ONE_TIME_AMOUNT, apiContext);
            return [
                monthlyNextAmountDueOption,
                waivedInterestOption,
                otherAmountOption
            ];
        }
        return [monthlyNextAmountDueOption, otherAmountOption];
    }
    if (isPN4(productType)) {
        const pn4NextAmountDueOption = createPaymentAmountOption(paymentOptions.PN4_NEXT_AMOUNT_DUE, apiContext);
        return [pn4NextAmountDueOption, otherAmountOption];
    }
    if (isSetpayLegacyProduct(productType)) {
        const totalAmountDueOption = createPaymentAmountOption(paymentOptions.TOTAL_AMOUNT_DUE, apiContext);
        return [totalAmountDueOption, otherAmountOption];
    }
    if (isSecuredInstallmentLoanProduct(productType)) {
        const securedInstallmentMonthlyPaymentOption = createPaymentAmountOption(paymentOptions.SECURED_INSTALLMENT_MONTHLY_PAYMENT_AMOUNT, apiContext);
        return [securedInstallmentMonthlyPaymentOption, otherAmountOption];
    }
    const currentBalanceOption = createPaymentAmountOption(paymentOptions.CURRENT_BALANCE, apiContext);
    const totalMinimumPaymentOption = createPaymentAmountOption(paymentOptions.MINIMUM_PAYMENT_DUE, apiContext);
    const statementBalanceOption = createPaymentAmountOption(paymentOptions.STATEMENT_BALANCE, apiContext);
    const pastDueAmountOption = createPaymentAmountOption(paymentOptions.PAST_DUE, apiContext);
    const fixedPaymentAmountOption = createPaymentAmountOption(paymentOptions.FIXED_PAYMENT, apiContext);
    const isPastDue = accountSummary.billing.pastDue &&
        Number(accountSummary.billing.pastDueAmount) > 0;
    if (accountSummary.billing.customerAssistancePlan) {
        const fixedPaymentAmountOptionArray = fixedPaymentAmountOption
            ? [fixedPaymentAmountOption]
            : [];
        if (!isPastDue)
            return [
                ...fixedPaymentAmountOptionArray,
                currentBalanceOption,
                statementBalanceOption,
                totalMinimumPaymentOption,
                otherAmountOption
            ];
        return [
            ...fixedPaymentAmountOptionArray,
            currentBalanceOption,
            statementBalanceOption,
            pastDueAmountOption,
            totalMinimumPaymentOption,
            otherAmountOption
        ];
    }
    if (isPastDue) {
        return [
            currentBalanceOption,
            totalMinimumPaymentOption,
            pastDueAmountOption,
            statementBalanceOption,
            otherAmountOption
        ];
    }
    return [
        currentBalanceOption,
        statementBalanceOption,
        totalMinimumPaymentOption,
        otherAmountOption
    ];
};
const getDefaultOptions = (amountOptions, paymentOption) => amountOptions.find(({ value }) => value === paymentOption);
const OneTimePaymentAmountSelect = ({ id }) => {
    const { helpNumber, featureFlags } = useContext(MainContext);
    const [apiMachine] = useApiMachine();
    const { data: accountSummary } = apiMachine.context.accountSummaryQueryResult;
    const { billing, product } = accountSummary;
    const { totalMinimumPaymentDue } = billing;
    const paymentOptionField = useField(PAYMENT_OPTION_INPUT_NAME);
    const paymentAmountField = useField(PAYMENT_AMOUNT_INPUT_NAME);
    const productType = product.type;
    const isOtherAmount = paymentOptionField.value === paymentOptions.OTHER_PAYMENT;
    const paymentAmount = isOtherAmount
        ? paymentAmountField.value
        : getPaymentAmountFromOption(paymentOptionField.value, apiMachine.context);
    const otherAmountKey = `${id}Other`;
    const amountOptions = getAmountOptions(apiMachine.context);
    const { isSettlement } = useDigitalCollections();
    const isSecuredInstallmentLoan = isSecuredInstallmentLoanProduct(productType);
    const getOtherAmountHelpText = () => {
        const oneTimeOtherAmountHelpText = getOneTimeOtherAmountHelpText(paymentAmountField.value, totalMinimumPaymentDue, isSettlement);
        if (isSecuredInstallmentLoan && oneTimeOtherAmountHelpText) {
            return t('makePayment.otherAmountHelpTextSILowerThanMonthlyDue', {
                paymentAmount: totalMinimumPaymentDue
            });
        }
        if (isSecuredInstallmentLoan) {
            return t('makePayment.otherAmountHelpTextSI', { helpNumber });
        }
        return oneTimeOtherAmountHelpText;
    };
    const [otherAmountHelpText, setOtherAmountHelpText] = useState(getOtherAmountHelpText);
    const paymentOptionChangeHandler = ({ value }) => {
        paymentOptionField.setValue(value);
        paymentOptionField.blur();
        if (value !== paymentOptions.OTHER_PAYMENT) {
            paymentAmountField.reset();
        }
    };
    const paymentAmountChangeHandler = ({ target }) => {
        paymentAmountField.setValue(target.value);
        setOtherAmountHelpText('');
    };
    const paymentAmountBlurHandler = () => {
        paymentAmountField.blur();
        setOtherAmountHelpText(getOtherAmountHelpText);
    };
    const paymentAmountPlaceholder = paymentOptionField.value
        ? t('makePayment.paymentAmount')
        : t('label.paymentAmountPlaceholder');
    const paymentAmountHelpText = getOneTimePaymentAmountHelpText(productType, accountSummary, paymentOptionField.value, featureFlags);
    return (_jsxs(_Fragment, { children: [!isSettlement && (_jsx(SelectWrapper, Object.assign({ "data-scroll": PAYMENT_OPTION_INPUT_NAME }, { children: _jsx(SelectWithFocus, { autoAdjust: true, dataCy: "payment-amount-field", defaultOption: getDefaultOptions(amountOptions, paymentOptionField.value), error: paymentOptionField.error, errorIcon: _jsx(FontAwesomeIcon, { icon: FAL_EXCLAMATION_CIRCLE, size: "1x" }), helpText: paymentAmountHelpText, id: id, isDisabled: paymentOptionField.isDisabled, leftIcon: _jsx(FontAwesomeIcon, { icon: FAR_DOLLAR_SIGN }), name: id, options: amountOptions, placeholder: paymentAmountPlaceholder, required: true, rightIconClosed: _jsx(FontAwesomeIcon, { icon: FAR_ANGLE_DOWN, size: "lg" }), rightIconOpen: _jsx(FontAwesomeIcon, { icon: FAR_ANGLE_UP, size: "lg" }), tabIndex: paymentOptionField.isDisabled ? -1 : 0, onChange: paymentOptionChangeHandler }) }))), (isOtherAmount || isSettlement) && (_jsx(SelectWrapper, Object.assign({ "data-scroll": PAYMENT_AMOUNT_INPUT_NAME }, { children: _jsx(MAPTextfield, { autoCapitalize: "none", autoComplete: "transaction-amount", dataCy: "other-amount-text-field", error: paymentAmountField.error, errorIcon: _jsx(FontAwesomeIcon, { icon: FAL_EXCLAMATION_CIRCLE, size: "1x" }), helpText: otherAmountHelpText, id: otherAmountKey, inputMode: "decimal", isDisabled: paymentAmountField.isDisabled, leftIcon: _jsx(FontAwesomeIcon, { icon: FAR_DOLLAR_SIGN }), matchPattern: /^[0-9]*(\.[0-9]{0,2})?$/, name: otherAmountKey, placeholder: t('text.otherAmountOption'), required: true, type: "text", value: paymentAmount, width: "100%", onBlur: paymentAmountBlurHandler, onChange: paymentAmountChangeHandler }) })))] }));
};
export default OneTimePaymentAmountSelect;
